import {
  ReactElement, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { colors } from 'styles/colors';
import { getUserProfile } from '~/helpers/userProfile';
import { useFilterContext } from '../contexts/FilterContext';
import { HealthCountInfo, useHealthDataContext } from '../contexts/HealthDataContext';
import { handleFilterStateRoute } from '../helpers/filters';
import { normalizeNumber } from '../helpers/numbers';
import { BigNumberCard } from './components/Card';
import { HealthyDescriptionTooltipContent } from './styles';

interface HealthyProps {
  hasHealth: boolean;
  loading: boolean;
}

export function Healthy({
  hasHealth,
  loading,
}: Readonly<HealthyProps>): ReactElement {
  const history = useHistory();
  const { t } = useTranslation();
  const { clients, selectedOptions } = useFilterContext();
  const {
    data,
    loading: loadingHealth,
    units,
  } = useHealthDataContext();
  const [profile] = useState(getUserProfile());
  const [healthInfo, setHealthInfo] = useState<{
    percentageHealthy: number,
    healthyUnits: number
  } | undefined>();

  const generalLoading = useMemo(
    () => loading || (loadingHealth && hasHealth),
    [hasHealth, loading, loadingHealth],
  );

  const hasPredictiveAccess = useMemo(() => {
    const ALLOWED_PREDICTIVE_CLIENT_IDS = new Set([145, 149]);

    return profile.permissions.PER_CLIENT.some(
      (item) => ALLOWED_PREDICTIVE_CLIENT_IDS.has(item.clientId),
    );
  }, [profile.permissions.PER_CLIENT]);

  function parseHealthInfo(data: HealthCountInfo | undefined): void {
    if (data) {
      const healthyUnits = data.healthy;
      const total = data.alert + data.critical + data.healthy;

      if (Number(total) === 0) {
        setHealthInfo({
          percentageHealthy: 0,
          healthyUnits: 0,
        });
        return;
      }

      const percentageHealthy = Math.floor(healthyUnits * 100 / total);

      setHealthInfo({
        percentageHealthy,
        healthyUnits,
      });
    }
  }

  function indicatorColor(): string {
    if (!healthInfo) return colors.icon['disabled-300'];

    if (healthInfo.percentageHealthy >= 85) return colors.state.success.base;
    if (healthInfo.percentageHealthy >= 45) return colors.state.warning.base;
    return colors.state.danger.base;
  }

  function onClickHealthDetails(): void {
    if (hasPredictiveAccess) {
      history.push('/analise/preditivo');
      return;
    }

    const filter = handleFilterStateRoute(clients, selectedOptions, profile);
    const unitIds = units
      .filter((unit) => unit.health === 'healthy')
      .map((unit) => unit.id.toString());

    if (unitIds.length) {
      filter.units = unitIds;
    }

    history.push(
      '/analise/unidades',
      filter,
    );
  }

  useEffect(() => {
    parseHealthInfo(data);
  }, [data]);

  const healthyUnitsCount = useMemo(
    () => units.filter((unit) => unit.health === 'healthy').length,
    [units],
  );

  const canSeeDetails = hasPredictiveAccess || healthyUnitsCount > 0;

  return (
    <BigNumberCard
      id="healthy"
      icon="ActivityIcon"
      number={`${normalizeNumber(healthInfo?.percentageHealthy)}%`}
      description={{
        text: t('unidadesSaudaveis'),
        tooltip: <HealthyTooltip />,
      }}
      subDescription={{
        number: normalizeNumber(healthInfo?.healthyUnits),
        text: t('unidadesSemAlerta').toLowerCase(),
      }}
      loading={generalLoading}
      disabledTooltip={generalLoading}
      dataTestId="big-number-card-healthy"
      indicatorColor={indicatorColor()}
      needUpgrade={hasHealth ? undefined : 'health'}
      onClickDetails={hasHealth && canSeeDetails ? onClickHealthDetails : undefined}
      detailsDisabledReasonKey={
        hasHealth && !hasPredictiveAccess && healthyUnitsCount === 0
          ? 'semUnidadesParaVerDetalhes'
          : undefined
      }
    />
  );
}

function HealthyTooltip(): ReactElement {
  const { t } = useTranslation();

  return (
    <HealthyDescriptionTooltipContent>
      <span>{t('unidadesOperacaoComBomDesempenhoDasMaquinas')}</span>
    </HealthyDescriptionTooltipContent>
  );
}
